import React from 'react';
import FormEditor from '../../../FormBuilder/FormEditor';
import '../../../FormBuilder/FormEditor.css';

const FormForm = ({ component, onUpdate }) => {
  // Extract form data from component's specificAttrs
  const formData = {
    title: component.specificAttrs?.title || 'Form',
    description: component.specificAttrs?.description || '',
    submitLabel: component.specificAttrs?.submitLabel || 'Submit',
    fields: component.specificAttrs?.fields || [],
    actions: component.specificAttrs?.actions || {
      onSubmit: 'save',
      successMessage: 'Form submitted successfully!',
      errorMessage: 'Please fix the errors and try again.',
      redirectUrl: ''
    },
    settings: component.specificAttrs?.settings || {
      showProgress: false,
      allowDraft: false,
      requireAuth: false
    }
  };

  // Handle form updates
  const handleFormUpdate = (updatedFormData) => {
    // Update the component's specificAttrs with the new form data
    onUpdate(component.id, {
      specificAttrs: {
        ...component.specificAttrs,
        ...updatedFormData,
        // Ensure we maintain the formType
        formType: component.specificAttrs?.formType || 'Custom'
      }
    });
  };

  return (
    <div className="form-form-container">
      <div className="form-form-header">
        <h4 style={{
          margin: '0 0 8px',
          fontSize: '14px',
          fontWeight: '600',
          color: '#111827'
        }}>
          📋 Form Configuration
        </h4>
        <p style={{
          margin: '0 0 16px',
          fontSize: '12px',
          color: '#6B7280'
        }}>
          Configure form fields, validation, and behavior. Changes will update the form preview in real-time.
        </p>
      </div>

      {/* Form Type Info */}
      <div style={{
        padding: '12px',
        background: '#EFF6FF',
        border: '1px solid #BFDBFE',
        borderRadius: '6px',
        marginBottom: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontSize: '13px'
        }}>
          <span style={{ fontWeight: '600', color: '#1E40AF' }}>Form Type:</span>
          <span style={{ color: '#3730A3' }}>
            {component.specificAttrs?.formType || 'Custom'}
          </span>
          <span style={{ color: '#6B7280' }}>•</span>
          <span style={{ color: '#6B7280' }}>
            {formData.fields.length} field{formData.fields.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Form Editor */}
      <FormEditor
        formData={formData}
        onFormUpdate={handleFormUpdate}
        showHeader={false}
        compact={true}
      />

      {/* Quick Actions */}
      <div style={{
        marginTop: '16px',
        padding: '12px',
        background: '#F9FAFB',
        border: '1px solid #E5E7EB',
        borderRadius: '6px'
      }}>
        <h5 style={{
          margin: '0 0 8px',
          fontSize: '13px',
          fontWeight: '600',
          color: '#374151'
        }}>
          Quick Actions
        </h5>
        <div style={{
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap'
        }}>
          <button
            type="button"
            onClick={() => {
              const newField = {
                id: `field_${Date.now()}`,
                label: 'New Text Field',
                type: 'text',
                required: false,
                validation: { customMessage: '' },
                dataSource: { type: 'manual' },
                visibility: { show: true, conditions: [] },
                styling: { width: '100%', placeholder: '' },
                defaultValue: '',
                helpText: ''
              };
              
              const updatedFormData = {
                ...formData,
                fields: [...formData.fields, newField]
              };
              
              handleFormUpdate(updatedFormData);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#059669',
              background: '#ECFDF5',
              border: '1px solid #A7F3D0',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            + Add Text Field
          </button>

          <button
            type="button"
            onClick={() => {
              const newField = {
                id: `field_${Date.now()}`,
                label: 'Email Address',
                type: 'email',
                required: true,
                validation: { 
                  pattern: 'email',
                  customMessage: 'Please enter a valid email address' 
                },
                dataSource: { type: 'manual' },
                visibility: { show: true, conditions: [] },
                styling: { width: '100%', placeholder: 'Enter your email' },
                defaultValue: '',
                helpText: ''
              };
              
              const updatedFormData = {
                ...formData,
                fields: [...formData.fields, newField]
              };
              
              handleFormUpdate(updatedFormData);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#7C3AED',
              background: '#F3E8FF',
              border: '1px solid #C4B5FD',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            + Add Email Field
          </button>

          <button
            type="button"
            onClick={() => {
              const newField = {
                id: `field_${Date.now()}`,
                label: 'Select Option',
                type: 'dropdown',
                required: false,
                validation: { customMessage: '' },
                dataSource: { 
                  type: 'manual',
                  options: ['Option 1', 'Option 2', 'Option 3']
                },
                visibility: { show: true, conditions: [] },
                styling: { width: '100%', placeholder: '' },
                defaultValue: '',
                helpText: ''
              };
              
              const updatedFormData = {
                ...formData,
                fields: [...formData.fields, newField]
              };
              
              handleFormUpdate(updatedFormData);
            }}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: '500',
              color: '#DC2626',
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            + Add Dropdown
          </button>
        </div>
      </div>

      {/* Form Statistics */}
      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        background: '#F3F4F6',
        borderRadius: '4px',
        fontSize: '11px',
        color: '#6B7280',
        textAlign: 'center'
      }}>
        {formData.fields.length} total field{formData.fields.length !== 1 ? 's' : ''} • 
        {formData.fields.filter(f => f.required).length} required • 
        {formData.fields.filter(f => f.visibility?.show !== false).length} visible
      </div>
    </div>
  );
};

export { FormForm };